import type {
  Comment,
  CreateCommentPayload,
  CreateThreadPayload,
  DeleteCommentResult,
  Thread,
  ThreadListQuery,
  UpdateCommentPayload,
  UpdateThreadPayload,
} from '@atlas/api-contract';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/errors';
import {
  createComment,
  createThread,
  deleteComment,
  listThreads,
  setThreadResolved,
  updateComment,
} from '@/lib/api/threads';
import { queryKeys } from '@/lib/query/keys';

import { removeThread } from './visible-threads';

/**
 * React Query bindings for threads and comments.
 *
 * ============================================================================
 * NOTHING HERE IS OPTIMISTIC (spec R5.7)
 * ============================================================================
 * Every mutation awaits the server's answer and only then invalidates the
 * thread list for the version being viewed. That is a deliberate refusal of the
 * usual instant-feedback pattern, for reasons specific to this feature:
 *
 * - A thread's identity comes from the server. An optimistic pin would have no
 *   id, so it could not be selected, highlighted, replied to or sent to the
 *   agent — `atlas:render-pins` is keyed by `threadId`. The "instant" version
 *   would be a marker that does nothing until the response lands anyway.
 * - Resolution is idempotent and last-write-wins across users. Showing a thread
 *   as resolved before the server agrees would routinely disagree with the
 *   colleague who reopened it a second earlier.
 * - These are other people's words. A reply that appears and then vanishes
 *   because the request failed is worse than one that takes 200 ms to appear.
 *
 * The single exception is not an exception: on `threadDeleted` the deleted
 * thread is dropped from the cached lists *after* the response says it is gone.
 * That is applying the server's answer, not predicting it.
 *
 * Every `onSuccess` returns its invalidation promise, so `mutateAsync` settles
 * only once the list has caught up and a composer can close knowing the panel
 * behind it is already right.
 */

/** Every mutation carries the version whose list it invalidates. */
const invalidateThreadLists = (
  queryClient: QueryClient,
  versionId: string,
): Promise<void> =>
  queryClient.invalidateQueries({
    queryKey: queryKeys.threads.lists(versionId),
  });

/**
 * Query options for one filter combination of one version's threads.
 *
 * Comments arrive nested, so this single request feeds the panel, every pin on
 * the canvas and every open popover. There is no per-thread query and no
 * per-thread cache entry — a thread is never addressed on its own, and adding a
 * second source for the same rows would mean two things to invalidate and two
 * chances for the popover and the pin under it to disagree.
 */
export const threadsQueryOptions = (
  versionId: string,
  query: ThreadListQuery,
) =>
  queryOptions<Thread[], ApiError>({
    queryKey: queryKeys.threads.list(versionId, query),
    queryFn: ({ signal }) => listThreads(versionId, query, { signal }),
  });

/**
 * `versionId` is nullable because the viewer has none until the version list
 * resolves, and a prototype nobody has uploaded to never gets one. React Query
 * needs a stable key either way, so the query is *disabled* rather than skipped
 * — the panel reads `isPending` the same way in both cases.
 */
export const useThreadsQuery = (
  versionId: string | null,
  query: ThreadListQuery,
): UseQueryResult<Thread[], ApiError> =>
  useQuery({
    ...threadsQueryOptions(versionId ?? '__none__', query),
    enabled: versionId !== null,
  });

export interface CreateThreadVariables {
  versionId: string;
  payload: CreateThreadPayload;
}

export const useCreateThread = (): UseMutationResult<
  Thread,
  ApiError,
  CreateThreadVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<Thread, ApiError, CreateThreadVariables>({
    mutationFn: ({ versionId, payload }) => createThread(versionId, payload),
    onSuccess: (_thread, { versionId }) =>
      invalidateThreadLists(queryClient, versionId),
  });
};

export interface SetThreadResolvedVariables {
  versionId: string;
  threadId: string;
  payload: UpdateThreadPayload;
}

export const useSetThreadResolved = (): UseMutationResult<
  Thread,
  ApiError,
  SetThreadResolvedVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<Thread, ApiError, SetThreadResolvedVariables>({
    mutationFn: ({ threadId, payload }) => setThreadResolved(threadId, payload),
    onSuccess: (_thread, { versionId }) =>
      invalidateThreadLists(queryClient, versionId),
  });
};

export interface CreateCommentVariables {
  versionId: string;
  threadId: string;
  payload: CreateCommentPayload;
}

export const useCreateComment = (): UseMutationResult<
  Comment,
  ApiError,
  CreateCommentVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<Comment, ApiError, CreateCommentVariables>({
    mutationFn: ({ threadId, payload }) => createComment(threadId, payload),
    onSuccess: (_comment, { versionId }) =>
      invalidateThreadLists(queryClient, versionId),
  });
};

export interface UpdateCommentVariables {
  versionId: string;
  commentId: string;
  payload: UpdateCommentPayload;
}

export const useUpdateComment = (): UseMutationResult<
  Comment,
  ApiError,
  UpdateCommentVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<Comment, ApiError, UpdateCommentVariables>({
    mutationFn: ({ commentId, payload }) => updateComment(commentId, payload),
    onSuccess: (_comment, { versionId }) =>
      invalidateThreadLists(queryClient, versionId),
  });
};

export interface DeleteCommentVariables {
  versionId: string;
  commentId: string;
}

/**
 * Deleting a comment, which sometimes deletes a thread.
 *
 * The response says which happened. When the thread went too, it is removed
 * from every cached list for this version *before* the invalidation is awaited:
 * the pin has to be gone the moment the dialog closes, and waiting for a
 * refetch would leave a marker on the canvas pointing at nothing.
 *
 * There is deliberately no follow-up read of the thread. It does not exist any
 * more — asking for it would be refetching into a 404.
 */
export const useDeleteComment = (): UseMutationResult<
  DeleteCommentResult,
  ApiError,
  DeleteCommentVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<DeleteCommentResult, ApiError, DeleteCommentVariables>({
    mutationFn: ({ commentId }) => deleteComment(commentId),
    onSuccess: (result, { versionId }) => {
      if (result.threadDeleted) {
        queryClient.setQueriesData<Thread[]>(
          { queryKey: queryKeys.threads.lists(versionId) },
          (threads) => removeThread(threads, result.threadId),
        );
      }

      return invalidateThreadLists(queryClient, versionId);
    },
  });
};
